function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { MENTION_CLASS, NO_MARGIN_CLASS } from '../constants';
import { decodeAttributes } from '../attribute-encoding';
import { BlockListParser, DocumentHtmlEntityDecoder } from '@intercom/interblocks.ts';
const AST_CLASSES_INVERSE = {
  'intercom-interblocks-align-left': 'left',
  'intercom-interblocks-align-center': 'center',
  'intercom-interblocks-align-right': 'right',
  'intercom-interblocks-align-justify': 'justify'
};
export default class TextBlockParser {
  constructor(documentBuilder, config) {
    _defineProperty(this, "blockAttrs", void 0);
    _defineProperty(this, "markMap", void 0);
    _defineProperty(this, "nodeMap", void 0);
    _defineProperty(this, "marks", ['b', 'i', 'a', 'code', 'u', 'span']);
    _defineProperty(this, "nodes", ['p', 'br', 'ol', 'ul', 'li', 'h1', 'h2', 'h3', 'h4', 'small', 'pre', 'img']);
    this.documentBuilder = documentBuilder;
    this.config = config;
    let schema = documentBuilder.schema;
    this.markMap = {
      b: schema.marks.bold,
      i: schema.marks.italic,
      a: schema.marks.anchor,
      code: schema.marks.code,
      u: schema.marks.underline,
      span: schema.marks.span
    };
    this.nodeMap = {
      p: schema.nodes.paragraph,
      ol: schema.nodes.orderedList,
      ul: schema.nodes.unorderedList,
      li: schema.nodes.listItem,
      h1: schema.nodes.heading,
      h2: schema.nodes.subheading,
      h3: schema.nodes.subheading3,
      h4: schema.nodes.subheading4,
      small: schema.nodes.small,
      pre: schema.nodes.codeBlock,
      br: schema.nodes.hardBreak,
      hr: schema.nodes.horizontalLine,
      template: schema.nodes.template,
      mention: schema.nodes.mention,
      aiPromptData: schema.nodes.aiPromptData,
      img: schema.nodes.inlineImage
    };
  }
  handle(block, nextBlock) {
    let ast = BlockListParser.parse([block], new DocumentHtmlEntityDecoder(document), !!this.nodeMap.template, this.config.isPlaintext, true, false, false);
    ast.body.forEach(node => this.handleNode(node, nextBlock));
  }
  handleNode(astNode, nextBlock) {
    if (astNode.isElement) {
      if (this.isMention(astNode)) {
        this.mention(astNode);
      } else if (this.isAiPromptData(astNode)) {
        this.aiPromptData(astNode);
      } else if (this.marks.includes(astNode.value)) {
        this.mark(astNode);
      } else if (this.nodes.includes(astNode.value)) {
        this.node(astNode, nextBlock);
      } else {
        throw new Error(`unknown element type: ${astNode.value}`);
      }
    } else if (astNode.isLiteral) {
      this.documentBuilder.addText(astNode.value);
    } else if (astNode.type === 'Template') {
      this.template(astNode);
    } else {
      this.stashBlockAttrs(astNode);
      this.handleChildren(astNode.children, nextBlock);
    }
  }
  node(astNode, nextBlock) {
    let nodeType = this.nodeMap[astNode.value];
    if (!nodeType) {
      return;
    }
    let attrs = this.collectAttrs(astNode);
    let blockAttrs = this.consumeBlockAttrs();
    this.addAlignment(attrs, blockAttrs);
    this.addStyle(attrs);
    if (nodeType === this.nodeMap.img) {
      let imgAlt = astNode.attributes.find(attr => attr.name === 'alt');
      if (imgAlt) {
        attrs.text = imgAlt.value;
      }
    }
    this.documentBuilder.openNode(nodeType, attrs);
    if (nodeType === this.nodeMap.li) {
      let paragraphType = this.nodeMap.p;
      this.documentBuilder.openNode(paragraphType, {});
    }
    this.handleChildren(astNode.children, nextBlock);
    if (nodeType === this.nodeMap.li) {
      this.documentBuilder.closeNode();
    }
    this.documentBuilder.closeNode();
    this.addExtraParagraphIfLegacyParagraph(astNode, blockAttrs, nextBlock);
  }
  mark(astNode) {
    let markType = this.markMap[astNode.value];
    let mark;
    if (markType && this.documentBuilder.top().type.allowsMarkType(markType)) {
      mark = markType.create(this.collectAttrs(astNode));
      this.documentBuilder.openMark(mark);
      if (markType === this.markMap.a) {
        this.handleInlineImageLinkUrl(mark, astNode);
      }
    }
    this.handleChildren(astNode.children);
    if (mark) {
      this.documentBuilder.closeMark(mark);
    }
  }
  mention(astNode) {
    let attrs = this.collectAttrs(astNode);
    if (this.nodeMap.mention) {
      this.documentBuilder.openNode(this.nodeMap.mention, {
        name: astNode.children[0].value,
        uri: attrs.href
      });
      this.documentBuilder.closeNode();
    }
  }
  aiPromptData(astNode) {
    let attrs = this.collectAttrs(astNode);

    // Convert data-* attributes to proper case
    let properCaseAttrs = {};
    for (let [key, value] of Object.entries(attrs)) {
      if (key.startsWith('data-prompt-data-')) {
        let parts = key.split('-');
        let camelKey = parts.slice(3) // Skip the 'data-prompt-data-' prefix
        .map((part, i) => i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)).join('');
        properCaseAttrs[camelKey] = value;
      }
    }
    attrs = properCaseAttrs;
    if (this.nodeMap.aiPromptData) {
      this.documentBuilder.openNode(this.nodeMap.aiPromptData, attrs);
      this.documentBuilder.closeNode();
    }
  }
  template(astNode) {
    if (this.nodeMap.template) {
      this.documentBuilder.openNode(this.nodeMap.template, {
        attributePath: astNode.value,
        fallback: astNode.fallback
      });
      this.documentBuilder.closeNode();
    }
  }
  handleInlineImageLinkUrl(mark, astNode) {
    if (astNode.children.length === 1 && astNode.children[0].value === 'img') {
      let linkUrl = mark.attrs.href;
      astNode.children[0].attributes.push({
        // eslint-disable-next-line @intercom/intercom/no-bare-strings
        type: 'Attribute',
        name: 'linkUrl',
        value: linkUrl
      });
    }
  }
  collectAttrs(astNode) {
    let attrs = {};
    astNode.attributes.forEach(n => {
      let value = decodeAttributes(n.value);
      if (attrs[n.name]) {
        attrs[n.name] += ` ${value}`;
      } else {
        attrs[n.name] = value;
      }
    });
    if (astNode.type === 'Block') {
      attrs['type'] = astNode.value;
    }
    if (astNode.isElement && astNode.id) {
      attrs['idAttribute'] = astNode.id;
    }
    return attrs;
  }
  stashBlockAttrs(astNode) {
    this.blockAttrs = this.collectAttrs(astNode);
  }
  consumeBlockAttrs() {
    let attrs = this.blockAttrs;
    this.blockAttrs = undefined;
    return attrs;
  }
  addAlignment(attrs, blockAttrs) {
    if (!blockAttrs || typeof blockAttrs.class !== 'string') {
      return;
    }
    blockAttrs.class.split(' ').forEach(name => {
      let alignment = AST_CLASSES_INVERSE[name];
      if (alignment) {
        Object.assign(attrs, {
          alignment
        });
      }
    });
  }
  addStyle(attrs) {
    if (!attrs || typeof attrs.style !== 'string') {
      return;
    }
    attrs.style.split(';').forEach(declaration => {
      if (!declaration) {
        return;
      }
      let propertyAndValue = declaration.split(':', 2);
      let property = propertyAndValue[0];
      let value = propertyAndValue[1].trim();
      if (property === 'font-family') {
        Object.assign(attrs, {
          fontFamily: value
        });
      }
      if (property === 'color') {
        Object.assign(attrs, {
          fontColor: value
        });
      }
      if (property === 'width') {
        Object.assign(attrs, {
          width: value.replace('px', '')
        });
      }
    });
  }
  handleChildren(children, nextBlock) {
    children.forEach(astNode => {
      this.handleNode(astNode, nextBlock);
    });
  }
  isMention(astNode) {
    return this.nodeMap.mention && astNode.value === 'a' && astNode.attributes.find(attr => attr.name === 'class' && attr.value === MENTION_CLASS);
  }
  isAiPromptData(astNode) {
    return this.nodeMap.aiPromptData && astNode.value === 'span' && astNode.attributes.find(attr => attr.name === 'data-ai-prompt-data');
  }
  addExtraParagraphIfLegacyParagraph(astNode, blockAttrs, nextBlock) {
    if (this.isParagraphWithImplicitMargin(astNode, blockAttrs, nextBlock) && !this.config.singleBlockMode && !this.config.allowImplicitMarginParagraphs) {
      this.documentBuilder.openNode(this.nodeMap.p, {});
      this.documentBuilder.closeNode();
    }
  }
  isParagraphWithImplicitMargin(astNode, blockAttrs, nextBlock) {
    if (!blockAttrs) {
      return false;
    }
    if (!nextBlock) {
      return false;
    }
    return astNode.value === 'p' && !blockAttrs.class.includes(NO_MARGIN_CLASS);
  }
}